/**
 * The chemistry coefficient scaffold (`chem.scaffold · coef`, surfaces.md §2.2,
 * §4.1): the skeleton written for her, rendered by KaTeX/mhchem, with a number
 * box before each species for its coefficient. Pure, like `slots.ts`.
 *
 * - **The skeleton is read from the prompt's last `$\ce{… -> …}$` span**, which
 *   the words render from `params.shown` (`${chem(eq)}$`); the browser has no
 *   params (errata E-client-9). The span is drawn by this surface, not twice.
 * - **Only the open coefficients are boxes** (mode `coef` opens every one, E-chem-3);
 *   `Tab`/`Shift+Tab` walk them and nothing else. **No box auto-advances**, so
 *   12 and 13 fit (surfaces.md §3.3).
 * - **Digits are read by §3.1's precedence**: a digit character is used as
 *   typed, a `Digit`/`Numpad` key carrying something else (the LT layout's
 *   `ą`…`ū`) is its digit by code (`ins_code`), and everything else is refused
 *   silently and kept in the trace: a letter (`refuse`, `not_in_slot`), a
 *   decimal mark (there is none in chemistry, §3.2), a sign.
 * - **An empty box is unanswered, never 1.** `Enter` with any box empty does
 *   not commit: it is logged as `enter_empty` with the box, and the caret goes
 *   to that box. The input would carry `""` (never absent, never `"1"`), but
 *   the judge's merge reads an empty coefficient as 1, so it is never sent
 *   (errata E-client-10). In a test, `Ctrl+Enter` skips as everywhere.
 *
 * `input` is `{"slots": {"L0.coef": "4", "L1.coef": "3", "R0.coef": "2"}}`,
 * open slots only, keyed by slot path, every leaf the string as left.
 */
import { spans } from "../../lib/math";
import { type KeyLike, digitOfCode, isBrowserChord, isInert, isPrintable, readChar } from "./keys";
import type { Action, Entry } from "./slots";

export interface Species {
  /** The slot path of its coefficient, `L0.coef` … `R1.coef`. */
  path: string;
  formula: string;
  /** A coefficient written into the skeleton (a locked slot), "" when open. */
  locked: string;
  open: boolean;
}

export interface Skeleton {
  left: Species[];
  right: Species[];
  /** mhchem's arrow as written: `->` or `<=>`. */
  arrow: string;
  /** The prompt with the skeleton's math span taken out. */
  prompt: string;
}

export interface ScaffoldState {
  values: Record<string, string>;
  active: number;
  caret: number;
}

export interface ScaffoldStep {
  state: ScaffoldState;
  entry: Entry | null;
  action: Action;
  native?: boolean;
}

const CE = /^\s*\\ce\{(.*)\}\s*$/s;
const ARROW = /\s*(<=>|<->|->|=)\s*/;

/** The skeleton of a `chem.scaffold` prompt, or null when the prompt has none (a content bug). */
export function parseSkeleton(prompt: string, mode = "coef"): Skeleton | null {
  const parts = spans(prompt);
  let at = -1;
  for (let i = parts.length - 1; i >= 0; i -= 1) {
    const p = parts[i];
    if (p.math && CE.test(p.tex) && ARROW.test(CE.exec(p.tex)![1])) {
      at = i;
      break;
    }
  }
  if (at < 0) return null;
  const body = CE.exec((parts[at] as { tex: string }).tex)![1];
  const m = ARROW.exec(body)!;
  const arrow = m[1] === "=" ? "->" : m[1];
  const lhs = body.slice(0, m.index);
  const rhs = body.slice(m.index + m[0].length);
  const side = (s: string, tag: "L" | "R"): Species[] =>
    s
      .split(/\s+\+\s+/)
      .map((x) => x.trim())
      .filter(Boolean)
      .map((x, i) => {
        const c = /^(\d+)\s*(.+)$/.exec(x);
        const open = mode === "coef" || mode === "custom";
        return { path: `${tag}${i}.coef`, formula: c ? c[2] : x, locked: open ? "" : (c?.[1] ?? ""), open };
      });
  const left = side(lhs, "L");
  const right = side(rhs, "R");
  if (!left.length || !right.length) return null;
  const rest = parts
    .filter((_, i) => i !== at)
    .map((p) => (p.math ? `$${p.tex}$` : p.text))
    .join("")
    .replace(/\s+$/, "");
  return { left, right, arrow, prompt: rest };
}

export function openPaths(sk: Skeleton): string[] {
  return [...sk.left, ...sk.right].filter((s) => s.open).map((s) => s.path);
}

export function initialScaffold(sk: Skeleton): ScaffoldState {
  const values: Record<string, string> = {};
  for (const p of openPaths(sk)) values[p] = "";
  return { values, active: 0, caret: 0 };
}

/** `input` for answer.submitted: the open slots only, each exactly as left. */
export function scaffoldInput(sk: Skeleton, st: ScaffoldState): { slots: Record<string, string> } {
  const slots: Record<string, string> = {};
  for (const p of openPaths(sk)) slots[p] = st.values[p] ?? "";
  return { slots };
}

/** The first open box she left empty, or null. An empty box is unanswered. */
export function firstEmpty(sk: Skeleton, st: ScaffoldState): number | null {
  const i = openPaths(sk).findIndex((p) => (st.values[p] ?? "") === "");
  return i < 0 ? null : i;
}

/** One keydown on the scaffold. The caller stamps the time, writes the trace and prevents the default unless `native`. */
export function scaffoldKey(sk: Skeleton, st: ScaffoldState, k: KeyLike, ctx: { test: boolean }): ScaffoldStep {
  const paths = openPaths(sk);
  const last = paths.length - 1;
  const path = paths[st.active];
  const cur = st.values[path] ?? "";
  const e = (op: string, x?: string): Entry => (x === undefined ? [k.code, k.key, path, op] : [k.code, k.key, path, op, x]);
  const go = (i: number, caret: number): ScaffoldState => ({ ...st, active: i, caret });

  if (k.key === "Enter") {
    if (k.repeat) return { state: st, entry: e("repeat"), action: null };
    if ((k.ctrlKey || k.metaKey) && ctx.test) return { state: st, entry: e("skip"), action: "skip" };
    const gap = firstEmpty(sk, st);
    if (gap !== null) return { state: go(gap, 0), entry: e("enter_empty", `empty:${paths[gap]}`), action: null };
    return { state: st, entry: e("enter"), action: "commit" };
  }
  if (isInert(k) || isBrowserChord(k)) return { state: st, entry: null, action: null, native: true };

  switch (k.key) {
    case "Tab":
      if (k.shiftKey) {
        if (st.active === 0) return { state: st, entry: null, action: null, native: true };
        return { state: go(st.active - 1, (st.values[paths[st.active - 1]] ?? "").length), entry: e("slot_prev"), action: null };
      }
      if (st.active === last) return { state: st, entry: null, action: null, native: true };
      return { state: go(st.active + 1, (st.values[paths[st.active + 1]] ?? "").length), entry: e("slot_next"), action: null };
    case "ArrowLeft":
      if (st.caret > 0) return { state: { ...st, caret: st.caret - 1 }, entry: e("caret"), action: null };
      if (st.active > 0) return { state: go(st.active - 1, (st.values[paths[st.active - 1]] ?? "").length), entry: e("caret"), action: null };
      return { state: st, entry: e("caret"), action: null };
    case "ArrowRight":
      if (st.caret < cur.length) return { state: { ...st, caret: st.caret + 1 }, entry: e("caret"), action: null };
      if (st.active < last) return { state: go(st.active + 1, 0), entry: e("caret"), action: null };
      return { state: st, entry: e("caret"), action: null };
    case "Home":
      return { state: go(0, 0), entry: e("caret"), action: null };
    case "End":
      return { state: go(last, (st.values[paths[last]] ?? "").length), entry: e("caret"), action: null };
    case "Backspace": {
      if (st.caret === 0) {
        if (st.active === 0) return { state: st, entry: e("bksp", "start"), action: null };
        return { state: go(st.active - 1, (st.values[paths[st.active - 1]] ?? "").length), entry: e("slot_prev"), action: null };
      }
      const next = cur.slice(0, st.caret - 1) + cur.slice(st.caret);
      return { state: { ...st, values: { ...st.values, [path]: next }, caret: st.caret - 1 }, entry: e("bksp", cur[st.caret - 1]), action: null };
    }
    case "Delete": {
      if (st.caret >= cur.length) return { state: st, entry: e("del", "end"), action: null };
      const next = cur.slice(0, st.caret) + cur.slice(st.caret + 1);
      return { state: { ...st, values: { ...st.values, [path]: next } }, entry: e("del", cur[st.caret]), action: null };
    }
  }
  if (!isPrintable(k) && k.key !== "Dead" && digitOfCode(k.code) === null) {
    return { state: st, entry: e("refuse", "not_in_slot"), action: null };
  }
  const r = readChar(k, (ch) => ch >= "0" && ch <= "9");
  if (r.kind === "refuse") return { state: st, entry: e("refuse", r.why), action: null };
  const next = cur.slice(0, st.caret) + r.ch + cur.slice(st.caret);
  return {
    state: { ...st, values: { ...st.values, [path]: next }, caret: st.caret + 1 },
    entry: r.kind === "code" ? e("ins_code", r.ch) : e("ins"),
    action: null,
  };
}

/** A paste: kept whole, whatever it holds (§3.3), one `paste` entry. */
export function scaffoldPaste(sk: Skeleton, st: ScaffoldState, text: string): ScaffoldStep {
  const path = openPaths(sk)[st.active];
  const one = text.replace(/[\r\n]+/g, " ");
  const cur = st.values[path] ?? "";
  return {
    state: { ...st, values: { ...st.values, [path]: cur.slice(0, st.caret) + one + cur.slice(st.caret) }, caret: st.caret + one.length },
    entry: ["", "", path, "paste", one],
    action: null,
  };
}

/** A click into a box. */
export function scaffoldClick(sk: Skeleton, st: ScaffoldState, index: number, caret: number): ScaffoldStep {
  const path = openPaths(sk)[index];
  const c = Math.max(0, Math.min(caret, (st.values[path] ?? "").length));
  return { state: { ...st, active: index, caret: c }, entry: ["Mouse0", `slot:${path}`, path, "focus"], action: null };
}
